import {Trans} from '@lingui/macro';
import {isEqual, noop} from 'lodash';
import React, {FC, ReactNode, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createSelector} from 'reselect';
import styled from 'styled-components';

import {getWidthOfElement} from '../../../../../libs/shared/helpers/src/browser/domHelpers';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {useSize} from '../../../../../libs/shared/ui/src/hooks/sizeHooks';
import {selectedSearchFilterKey} from '../../../../../srcCommon/inboxes/search/searchHelpers';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {AppSelector} from '../../../../helpers/reselectHelpers';
import {SearchHintModel} from '../../../../models/api/searchHintModel';
import {getInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {getLenses} from '../../../../store/data/lenses/lensesSelectors';
import {updateSelectedFilterAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getStarredTagIds, getTags} from '../../../../store/data/tags/tagsSelectors';
import {getFindTeammate} from '../../../../store/data/teammates/lazyTeammatesSelectors';
import {getTeams} from '../../../../store/data/teams/teamsSelectors';
import {CellsPanelPageTypesEnum} from '../../../../store/panels/cells/cellsPanelState';
import {CellsContext} from '../../../../store/panels/cells/context/cellsContext';
import {maybeUpdateSearchSessionTimestampAction} from '../../../../store/panels/cells/search/cellsPanelSearchActions';
import {
  getCellsSearchHints,
  getCellsSearchQueryHash,
} from '../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {toggleSearchQueryBuilderDropdownAction} from '../../../../store/panels/inboxes/inboxesPanelActions';
import {InboxesConversationsParametersWithSearch} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {buildParametersSearchLocalFilterName} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersHelpers';
import {
  getParametersSearchLocalFilterIcon,
  SearchLocalFilterIconType,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersIconHelpers';
import {getCellsPanel} from '../../../../store/panels/panelsSelectors';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';
import {searchByParametersAction} from '../../../../store/stateless/searchActions';
import {trackSearchHintClick} from '../searchTrackingHelpers';
import {SearchHintItem} from './searchHintItem';
import {makeSearchFiltersVisualHint, serializeToVisualHint, VisualSearchHint} from './searchHintsHelpers';

/*
 * Constants.
 */

const SEARCH_HINT_BAR_PADDING = 8;
const SEARCH_HINT_GAP = 8;

/*
 * Props.
 */

interface SearchHintsBarProps {
  inboxesPanelId: InboxesPanelId;
  parameters: InboxesConversationsParametersWithSearch;
}

/*
 * Styles.
 */

const StyledButtonWrapper = styled.div``;

const StyledSearchHintsBarWrapper = styled.div`
  display: flex;
  // Padding bottom is 8px because we have a 1px border.
  padding: ${SEARCH_HINT_BAR_PADDING}px;

  background-color: ${(p) => p.theme.greys.white};

  > *:not(:first-child) {
    margin-left: ${SEARCH_HINT_GAP}px;
  }

  ${StyledButtonWrapper} {
    margin-left: auto;
  }

  grid-area: filters;
  padding-left: 14px;
  overflow: hidden;
`;

const StyledVisuallyHidden = styled(VisuallyHidden)`
  display: flex;
`;

/*
 * Component.
 */

export const SearchHintsBar: FC<SearchHintsBarProps> = ({inboxesPanelId, parameters}) => {
  const [getSearchLocalFilterName, getSearchLocalFilterIcon] = useMemo(
    () => [makeGetSearchLocalFilterName(parameters), makeGetSearchLocalFilterIcon(parameters)],
    [parameters],
  );
  const localSearchFilterName = useAppSelector(getSearchLocalFilterName);
  const localSearchFilterIcon = useAppSelector(getSearchLocalFilterIcon);

  const [cellsContext, searchHints, isLoading] = useAppSelector(getSearchHintsStateProps);
  const queryHash = useAppSelector(getCellsSearchQueryHash);

  const dispatch = useAppDispatch();

  const localize = useLocalize();

  const computedSearchHints: ReadonlyArray<VisualSearchHint> = useMemo(
    () =>
      computeVisualSearchHints(
        localize,
        parameters,
        localSearchFilterName,
        localSearchFilterIcon,
        searchHints,
      ),
    [localize, parameters, localSearchFilterName, localSearchFilterIcon, searchHints],
  );

  // Always display the search filters hint at the beginning.
  const [displayedHints, setDisplayedHints] = useState<ReadonlyArray<VisualSearchHint>>([
    computedSearchHints[0],
  ]);

  const searchHintsBarWrapperRef = useRef<HTMLDivElement>(null);
  const searchHintsBarWrapperSizeInfo = useSize(searchHintsBarWrapperRef);
  const searchHintsBarWrapperWidth =
    (searchHintsBarWrapperSizeInfo && searchHintsBarWrapperSizeInfo.width) || null;
  const invisibleRenderRef = useRef<HTMLDivElement>(null);

  // We may need to remove some search hints if the width is too small.
  useLayoutEffect(() => {
    if (!invisibleRenderRef.current || !searchHintsBarWrapperWidth) {
      return;
    }

    // Subtract the horizontal padding.
    const availableWidth = searchHintsBarWrapperWidth - 2 * SEARCH_HINT_BAR_PADDING;

    const rootElement = invisibleRenderRef.current;
    const children = Array.from(rootElement.children);
    const childrenWidths = children.map((e, index) => ({
      index,
      width: Math.ceil(getWidthOfElement(e)),
    }));
    const childrenTotalWidth = childrenWidths.reduce(
      (acc, curr, index) => acc + curr.width + (index > 0 ? SEARCH_HINT_GAP : 0),
      0,
    );

    // If we have enough width, render all the computed hints.
    if (childrenTotalWidth <= availableWidth) {
      // Avoid a cascading render.
      setDisplayedHints((hints) => {
        if (isEqual(hints, computedSearchHints)) {
          return hints;
        }

        return computedSearchHints;
      });
    } else if (childrenWidths.length > 0) {
      // The last child is the "More filters" button, which should always be visible.
      const moreFiltersButtonWidth = childrenWidths[childrenWidths.length - 1];
      // Subtract the width of the "More filters" button.
      const availableHintsWidth = availableWidth - (moreFiltersButtonWidth.width + SEARCH_HINT_GAP);

      let hintsCurrentWidth = 0;

      const hintsToBeDisplayed = computedSearchHints.reduce<ReadonlyArray<VisualSearchHint>>(
        (acc, curr, index) => {
          const {width} = childrenWidths[index];

          // Update the width of displayed hints by adding the current hint width.
          hintsCurrentWidth += width + (index > 0 ? SEARCH_HINT_GAP : 0);

          // We always want to display the first (search filters) hint.
          if (index === 0) {
            return [...acc, curr];
          }

          // If the current width is less than the available one, display the current hint.
          if (hintsCurrentWidth <= availableHintsWidth) {
            return [...acc, curr];
          }

          return acc;
        },
        [],
      );

      // Avoid a cascading render.
      setDisplayedHints((hints) => {
        if (isEqual(hints, hintsToBeDisplayed)) {
          return hints;
        }

        return hintsToBeDisplayed;
      });
    }
  }, [computedSearchHints, searchHintsBarWrapperWidth]);

  const {
    search: {query: searchQuery},
  } = parameters;

  /**
   * When this method is called, the `hint` properties are the ones when the click was made.
   * Thus, if an user clicked on the hint to enable "Has attachment" for example, `isSelected` is still falsy here.
   */
  const onSearchHintClick = (hint: VisualSearchHint, selectedIndex = 0) => {
    const {type, values, selectedValueIndex, isTransient, onClick} = hint;

    const {modifier, value} = values[selectedIndex];

    // Maybe update search session timestamp.
    // It would be undefined if the user did not interact with the search bar before.
    dispatch(maybeUpdateSearchSessionTimestampAction());

    if (onClick) {
      const newSearchParameters = onClick(hint, selectedIndex);

      // If `location` changed, save it in localStorage.
      if (newSearchParameters.location) {
        dispatch(updateSelectedFilterAction(selectedSearchFilterKey, newSearchParameters.location));
      }

      // Track.
      trackSearchHintClick({
        type,
        queryHash,
      });

      // Navigate to search with updated search parameters.
      dispatch(
        searchByParametersAction(
          {
            ...parameters.search,
            ...newSearchParameters,
          },
          cellsContext,
          {
            isFolderSwitch: isDefined(newSearchParameters.location),
          },
        ),
      );
    } else if (modifier) {
      const enabledText = `${modifier}:${value}`;
      const disabledText = isTransient ? '' : value;

      // If hint was not enabled, clicking on it will "enrich" the query.
      const shouldEnrichQuery = selectedValueIndex === -1;

      // If hint was enabled but the value changed, we need to remove the former one and replace it by the new one.
      const shouldChangeValue = !shouldEnrichQuery && selectedValueIndex !== selectedIndex;

      /**
       * Update the query.
       *
       * If we should enrich the query, we need to check if the hint is a transient one.
       *   - if this is the case, we add `enabledText` at the end of the query (because it won't replace anything).
       *   - if this is not the case, we replace `disabledText` by `enabledText`.
       * If we should not, we need to check if the hint was already selected but its value had changed.
       *   - it this is the case, we need to replace former value by the new one.
       *   - if this is not the case, it means we need to replace `enabledText` by `disabledText`.
       */

      let updatedQuery: string;

      if (shouldEnrichQuery) {
        if (isTransient) {
          updatedQuery = `${searchQuery} ${enabledText}`;
        } else {
          updatedQuery = searchQuery.replace(disabledText, enabledText);
        }
      } else if (shouldChangeValue) {
        updatedQuery = searchQuery.replace(
          `${values[selectedValueIndex].modifier}:${values[selectedValueIndex].value}`,
          enabledText,
        );
      } else {
        updatedQuery = searchQuery.replace(enabledText, disabledText);
      }

      // Track.
      trackSearchHintClick({
        type,
        shouldEnrichQuery,
        queryHash,
      });

      // Navigate to search with updated query.
      const trimmedUpdatedQuery = updatedQuery.trim();
      dispatch(
        searchByParametersAction(
          trimmedUpdatedQuery.length === 0
            ? undefined
            : {
                ...parameters.search,
                query: trimmedUpdatedQuery,
              },
          cellsContext,
        ),
      );
    }
  };

  const onMoreFiltersClick: Handler = () => {
    // Maybe update search session timestamp.
    // It would be undefined if the user did not interact with the search bar before.
    dispatch(maybeUpdateSearchSessionTimestampAction());

    // Open the search query builder dropdown.
    dispatch(toggleSearchQueryBuilderDropdownAction(inboxesPanelId, 'hints-bar'));
  };

  const renderHints = (hints: ReadonlyArray<VisualSearchHint>) => (
    <>
      {hints.map((hint) => (
        <SearchHintItem
          key={hint.type}
          hint={hint}
          isDisabled={isLoading}
          onClick={isLoading ? noop : onSearchHintClick}
        />
      ))}
      {inboxesPanelId && !isLoading && (
        <Button
          variant={ButtonVariantsEnum.LINK}
          onClick={onMoreFiltersClick}
          interactionId="search-hint-more-filters"
        >
          <Trans id="search-hint-more-filters">More filters</Trans>
        </Button>
      )}
    </>
  );

  return (
    <StyledSearchHintsBarWrapper ref={searchHintsBarWrapperRef}>
      {renderHints(displayedHints)}
      <StyledVisuallyHidden ref={invisibleRenderRef}>{renderHints(computedSearchHints)}</StyledVisuallyHidden>
    </StyledSearchHintsBarWrapper>
  );
};

/*
 * Helpers.
 */

export function makeGetSearchLocalFilterName(inboxesParameters: InboxesConversationsParametersWithSearch) {
  return createSelector(
    [getInboxes, getTags, getLenses, getFindTeammate, getTeams],
    (inboxes, tags, lenses, findTeammate, teams) => {
      const options = {
        shouldWrapAllNamesInQuotes: false,
        shouldWrapProperNounsInQuotes: true,
      };

      return buildParametersSearchLocalFilterName(
        inboxes,
        tags,
        lenses,
        findTeammate,
        teams,
        inboxesParameters,
        options,
      );
    },
  );
}

function makeGetSearchLocalFilterIcon(inboxesParameters: InboxesConversationsParametersWithSearch) {
  return createSelector(
    [getInboxes, getTags, getStarredTagIds, getLenses, getTeams, getFindTeammate],
    (inboxes, tags, starredTagIds, lenses, teams, findTeammate) =>
      getParametersSearchLocalFilterIcon(
        inboxes,
        tags,
        lenses,
        starredTagIds,
        teams,
        findTeammate,
        inboxesParameters,
      ),
  );
}

const getSearchHintsStateProps: AppSelector<
  [CellsContext | undefined, ReadonlyArray<SearchHintModel> | undefined, boolean]
> = createSelector([getCellsPanel, getCellsSearchHints], (panel, searchHints) => {
  if (!panel) {
    return [undefined, undefined, false];
  }

  const activePage = panel.pages.current || panel.pages.next;

  if (!activePage || activePage.type !== CellsPanelPageTypesEnum.CONVERSATIONS) {
    return [undefined, undefined, false];
  }

  const {isLoading, results} = searchHints || {};

  return [activePage.cellsContext, results || undefined, isLoading || false];
});

function computeVisualSearchHints(
  localize: Localizer,
  parameters: InboxesConversationsParametersWithSearch,
  localSearchFilterName: ReactNode,
  localSearchFilterIcon: SearchLocalFilterIconType,
  searchHints?: ReadonlyArray<SearchHintModel>,
) {
  return (
    [
      makeSearchFiltersVisualHint(
        parameters.search.location,
        localSearchFilterName,
        localSearchFilterIcon,
        localize,
      ),
      ...(searchHints || []).map((hint) => serializeToVisualHint(hint, parameters.search.query, localize)),
    ]
      // Sort hints by priority, then by title.
      .sort((a, b) => b.priority - a.priority || (a.title && b.title && a.title.localeCompare(b.title)) || 0)
  );
}
